import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../service/apiRequest';
import {
    Table,
    Button,
    Flex,
    Breadcrumb,
    Form,
    message,
    Popconfirm,
    Space,
    Pagination,
    Switch,
    TableProps,
    Select,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { showSuccess, showError } from '../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../hooks/useCurrentModule';

const useStyle = createStyles(({ css }) => ({
    customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface EmployeeAttendanceRecord {
    attendance_id: number;
    attendance_ledger_id: number;
    attendance_date: string;
    attendance_in: string;
    attendance_out: string;
    attendance_type: string;
    attendance_remark: string;
}

interface EmployeeLeaveRecord {
    leave_id: number;
    leave_ledger_id: number;
    leave_type: string;
    leave_attendance_type: string;
    leave_date_start: string;
    leave_date_end: string;
    leave_remark: string;
    leave_status: string;
    ledger_first_name: string;
    ledger_last_name: string;
    leave_created_on: string;
}

const LeaveRequest: React.FC = () => {
    const [leaves, setLeaves] = useState<EmployeeLeaveRecord[]>([]);
    const [form] = Form.useForm();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [initialValues, setInitialValues] = useState<EmployeeLeaveRecord | null>(null);
    const { styles } = useStyle();
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const currentModule = useCurrentModule();
    const [attendanceData, setAttendanceData] = useState<EmployeeAttendanceRecord[]>([]);


    const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
    const [sortedInfo, setSortedInfo] = useState<any>({});


    const cancel: PopconfirmProps['onCancel'] = (e) => {
        console.log(e);
        showError('Click on No');
    };

    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    const fetchLeave = async () => {
        try {
            const data = await apiRequest<EmployeeLeaveRecord[]>({
                method: 'get',
                url: 'get-leave-requests',
            });
            setLeaves(data);
        } catch (error) {
            showError('Failed to fetch leave');
            console.error(error);
        }
    };


    useEffect(() => {
        fetchLeave();
    }, []);

    const handleEditClick = (record: EmployeeLeaveRecord) => {
        setIsEditMode(true);
        setInitialValues({
            leave_id: record.leave_id,
            leave_ledger_id: record.leave_ledger_id,
            leave_type: record.leave_type,
            leave_attendance_type: record.leave_attendance_type,
            leave_date_start: record.leave_date_start,
            leave_date_end: record.leave_date_end,
            leave_status: record.leave_status,
            leave_remark: record.leave_remark,
            ledger_first_name: record.ledger_first_name,
            ledger_last_name: record.ledger_last_name,
            leave_created_on: record.leave_created_on,
        });
        setIsModalVisible(true);
    };

    const handleCreate = () => {
        fetchLeave();
    };

    // const showCreateModal = () => {
    //     setIsEditMode(false);
    //     setInitialValues(null);
    //     setIsModalVisible(true);
    // };

    const hideCreateModal = () => {
        setIsModalVisible(false);
        setIsEditMode(false);
        setInitialValues(null);
    };

    const handleDelete = async (leave_id: number) => {
        try {
            await apiRequest({
                method: 'delete',
                url: `/delete-book/${leave_id}`,
            });
            showSuccess('leave reuqest deleted');
            fetchLeave();
        } catch {
            showError('Failed to delete leave reuqest');
        }
    };

    const handleStatusToggle = async (LeaveId: number, newStatus?: string) => {
        // Optimistically update the UI
        setLeaves((prev) =>
            prev.map((item) => {
                if (item.leave_id === LeaveId) {
                    return { ...item, leave_status: newStatus || item.leave_status };
                }
                return item;
            })
        );

        try {
            const response = await apiRequest({
                method: 'put',
                url: `/update-status-leave/${LeaveId}`,
                data: { leave_status: newStatus },
            });

            showSuccess('Leave request status updated to ' + response.newStatus);
        } catch (error) {
            showError('Failed to update status');
            // Optionally revert the status if API fails
        }
    };



    const handleTableChange: TableProps<EmployeeLeaveRecord>['onChange'] = (pagination, filters, sorter) => {
        setFilteredInfo(filters);
        setSortedInfo(sorter);
    };




    const columns: TableColumnsType<EmployeeLeaveRecord> = [
        {
            title: 'Name',
            width: 200,
            dataIndex: 'ledger_first_name', // can use first name as reference
            key: 'ledger_name', // unique key for this column
            filters: Array.from(new Set(
                leaves.map((s) => `${s.ledger_first_name} ${s.ledger_last_name}`)
            )).map((name) => ({
                text: name,
                value: name,
            })),
            filterSearch: true,
            filterMode: 'tree',
            filteredValue: filteredInfo.ledger_name || null,
            onFilter: (value, record) =>
                `${record.ledger_first_name} ${record.ledger_last_name}`.includes(value as string),
            sorter: (a, b) =>
                `${a.ledger_first_name} ${a.ledger_last_name}`.localeCompare(
                    `${b.ledger_first_name} ${b.ledger_last_name}`
                ),
            sortOrder: sortedInfo.columnKey === 'ledger_name' ? sortedInfo.order : null,
            sortDirections: ['ascend', 'descend'],
            ellipsis: true,
            render: (_, record) => (
                <div>{`${record.ledger_first_name} ${record.ledger_last_name}`}</div>
            ),
        },
        {
            title: 'Leave Request',
            dataIndex: 'leave_date_start',
            key: 'leave_request',
            width: 150,
            filters: Array.from(new Set(leaves.map((s) => s.leave_date_start))).map((date) => ({
                text: new Date(date).toLocaleDateString('en-GB'),
                value: date,
            })),
            filterSearch: true,
            filterMode: 'tree',
            filteredValue: filteredInfo.leave_date_start || null,
            onFilter: (value, record) => record.leave_date_start.includes(value as string),
            sorter: (a, b) => new Date(a.leave_date_start).getTime() - new Date(b.leave_date_start).getTime(),
            sortOrder: sortedInfo.columnKey === 'leave_date_start' ? sortedInfo.order : null,
            sortDirections: ['ascend', 'descend'],
            ellipsis: true,
            render: (_, record) => {
                const startDate = new Date(record.leave_date_start);
                const endDate = record.leave_date_end ? new Date(record.leave_date_end) : null;

                const formatDate = (date: Date) => {
                    const day = String(date.getDate()).padStart(2, '0');
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const year = date.getFullYear();
                    return `${day}-${month}-${year}`;
                };

                const totalDays = endDate
                    ? Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1
                    : 1; // if no end date, total days = 1

                return (
                    <div>
                        <div><strong>Start:</strong> {formatDate(startDate)}</div>
                        {endDate && <div><strong>End:</strong> {formatDate(endDate)}</div>}
                        <div><strong>Total Days:</strong> {totalDays}</div>
                    </div>
                );
            },
        },
        {
            title: 'Remark',
            dataIndex: 'leave_remark',
            key: 'leave_remark',
            width: 400,
            filters: Array.from(new Set(leaves.map((s) => s.leave_remark))).map((name) => ({
                text: name,
                value: name,
            })),
            filterSearch: true,
            filterMode: 'tree',
            filteredValue: filteredInfo.leave_remark || null,
            onFilter: (value, record) => record.leave_remark.includes(value as string),
            sorter: (a, b) => a.leave_remark.localeCompare(b.leave_remark),
            sortOrder: sortedInfo.columnKey === 'leave_remark' ? sortedInfo.order : null,
            sortDirections: ['ascend', 'descend'],
            ellipsis: true,
            render: (_, record) => <div>{record.leave_remark}</div>,
        },
        {
            title: 'Attendance Type',
            dataIndex: 'leave_attendance_type',
            key: 'leave_attendance_type',
            width: 150,
            filters: Array.from(new Set(leaves.map((s) => s.leave_attendance_type))).map((name) => ({
                text: name,
                value: name,
            })),
            filterSearch: true,
            filterMode: 'tree',
            filteredValue: filteredInfo.leave_attendance_type || null,
            onFilter: (value, record) => record.leave_attendance_type.includes(value as string),
            sorter: (a, b) => a.leave_attendance_type.localeCompare(b.leave_attendance_type),
            sortOrder: sortedInfo.columnKey === 'leave_attendance_type' ? sortedInfo.order : null,
            sortDirections: ['ascend', 'descend'],
            ellipsis: true,
            render: (_, record) => <div>{record.leave_attendance_type}</div>,
        },
        {
            title: 'Created On',
            dataIndex: 'leave_created_on',
            key: 'leave_created_on',
            width: 150,
            filters: Array.from(new Set(leaves.map((s) => s.leave_created_on))).map((date) => ({
                text: new Date(date).toLocaleDateString('en-GB'), // optional: show date in filter
                value: date,
            })),
            filterSearch: true,
            filterMode: 'tree',
            filteredValue: filteredInfo.leave_created_on || null,
            onFilter: (value, record) => record.leave_created_on.includes(value as string),
            sorter: (a, b) => new Date(a.leave_created_on).getTime() - new Date(b.leave_created_on).getTime(),
            sortOrder: sortedInfo.columnKey === 'leave_created_on' ? sortedInfo.order : null,
            sortDirections: ['ascend', 'descend'],
            ellipsis: true,
            render: (_, record) => {
                if (!record.leave_created_on) return null;

                const date = new Date(record.leave_created_on);

                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();

                let hours = date.getHours();
                const minutes = String(date.getMinutes()).padStart(2, '0');
                const ampm = hours >= 12 ? 'PM' : 'AM';
                hours = hours % 12 || 12; // convert to 12-hour format

                return (
                    <div>
                        <strong>Date : </strong>{`${day}-${month}-${year}`}{" "}<br/>
                        <strong>Time : </strong>{`${hours}:${minutes} ${ampm}`}
                    </div>
                );
            },
        },
    ];

    if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
        columns.push({
            title: 'Action',
            dataIndex: 'leave_status',
            key: 'leave_status',
            width: 100,
            filters: ["Pending", "Confirm", "Cancle"].map((status) => ({
                text: status,
                value: status,
            })),
            filterSearch: true,
            filteredValue: filteredInfo.leave_status || null,
            onFilter: (value, record) => record.leave_status === value,
            sorter: (a, b) => {
                const order = ["Pending", "Confirm", "Cancle"];
                return order.indexOf(a.leave_status) - order.indexOf(b.leave_status);
            },
            sortOrder: sortedInfo.columnKey === 'leave_status' ? sortedInfo.order : null,

            render: (_, record) => {
                const statusColors: Record<string, string> = {
                    Pending: 'Black',
                    Confirm: 'Black',
                    Cancle: 'Black',
                };

                return (
                    <Space>
                        {currentModule?.right_edit === 'Y' && (
                            <Select
                                value={record.leave_status}
                                style={{
                                    width: 130,
                                    fontWeight: "600",
                                    textAlign: "center",
                                }}
                                onChange={(value) =>
                                    handleStatusToggle(record.leave_id, value)
                                }
                                options={[
                                    {
                                        value: 'Pending',
                                        label: (
                                            <span style={{ color: statusColors.Pending }}>
                                                Pending
                                            </span>
                                        ),
                                    },
                                    {
                                        value: 'Confirm',
                                        label: (
                                            <span style={{ color: statusColors.Confirm }}>
                                                Confirm
                                            </span>
                                        ),
                                    },
                                    {
                                        value: 'Cancle',
                                        label: (
                                            <span style={{ color: statusColors.Cancle }}>
                                                Cancle
                                            </span>
                                        ),
                                    },
                                ]}
                            />
                        )}

                        {/* {currentModule?.right_delete === 'Y' && (
                            <Popconfirm
                                title="Delete this leave request?"
                                onConfirm={() => handleDelete(record.leave_id)}
                                okText="Yes"
                                cancelText="No"
                            >
                                <Button danger>
                                    <DeleteOutlined />
                                </Button>
                            </Popconfirm>
                        )} */}
                    </Space>
                );
            },
        });
    }



    return (
        <>
            <Space
                style={{
                    display: 'flex',
                    marginBottom: 16,
                    alignItems: 'center',
                    width: '100%',
                    justifyContent: 'space-between',
                }}
            >
                <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
                    <Breadcrumb.Item href="/">
                        <HomeOutlined />
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>View Leave Reuqests</Breadcrumb.Item>
                </Breadcrumb>
            </Space>


            <div
                style={{
                    minHeight: 360,
                    background: colorBgContainer,
                    borderRadius: borderRadiusLG,
                }}
            >
                <Table<EmployeeLeaveRecord>
                    tableLayout="auto"
                    className={`${styles.customTable} scrollable-table`}
                    columns={columns}
                    onChange={handleTableChange}
                    rowKey="leave_id"
                    dataSource={leaves.slice(
                        (pagination.current - 1) * pagination.pageSize,
                        pagination.current * pagination.pageSize
                    )}
                    bordered
                    pagination={false}
                    scroll={{ y: 37 * 11 }}
                    locale={{ emptyText: 'No leave reuqest found' }}
                    footer={() => (
                        <Flex justify="space-between">
                            <span>
                                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                                {Math.min(pagination.current * pagination.pageSize, leaves.length)} of{' '}
                                {leaves.length}
                            </span>
                            <Pagination
                                {...pagination}
                                total={leaves.length}
                                showSizeChanger
                                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
                            />
                        </Flex>
                    )}
                />
            </div>
        </>
    );
};

export default LeaveRequest;